import { useEffect, useRef } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { useToast } from '../../contexts/ToastContext';

// Watches campaign-level state and fires toasts on noteworthy transitions.
// Renders nothing — pure side-effect adapter. Mount once inside both providers.
export default function ToastEvents() {
  const { players, initiative, traps } = useCampaign();
  const { addToast } = useToast();

  // Player joined — detect new ids in the players map.
  const prevPlayerIdsRef = useRef(null);
  useEffect(() => {
    const ids = new Set(Object.keys(players || {}));
    const prev = prevPlayerIdsRef.current;
    if (prev) {
      for (const id of ids) {
        if (!prev.has(id)) {
          const p = players[id];
          if (p?.name) addToast({ message: `${p.name} joined the session`, icon: '🚪', type: 'success' });
        }
      }
    }
    prevPlayerIdsRef.current = ids;
  }, [players, addToast]);

  // Combat lifecycle — fire on phase transitions in/out of 'active'.
  const prevPhaseRef = useRef(null);
  useEffect(() => {
    const phase = initiative?.phase ?? null;
    const prev = prevPhaseRef.current;
    if (prev !== null && prev !== phase) {
      if (phase === 'active' && prev !== 'active') {
        addToast({ message: 'Combat begins', icon: '⚔', type: 'warn' });
      }
      if (prev === 'active' && phase !== 'active') {
        addToast({ message: 'Combat ended', icon: '☮', type: 'success' });
      }
    }
    prevPhaseRef.current = phase;
  }, [initiative?.phase, addToast]);

  // Trap triggered — detect any trap transitioning to triggered:true.
  // Fires on every view (DM, player, Display) so everyone at the table sees
  // the name of the trap that just sprung.
  const prevTriggeredRef = useRef(null);
  useEffect(() => {
    const triggered = new Set();
    for (const id in (traps || {})) {
      if (traps[id]?.triggered) triggered.add(id);
    }
    const prev = prevTriggeredRef.current;
    if (prev) {
      for (const id of triggered) {
        if (!prev.has(id)) {
          const t = traps[id];
          addToast({
            message: `${t?.label || 'Trap'} sprung!`,
            icon: '🪤',
            type: 'danger',
            duration: 5000,
          });
        }
      }
    }
    prevTriggeredRef.current = triggered;
  }, [traps, addToast]);

  return null;
}
